// =============================================================================
// AudioEngine.ts — Trilha DINÂMICA e efeitos, 100% sintetizados via WebAudio.
//
// A música é um sequenciador chiptune (16ths, agendamento preciso com
// lookahead): bumbo de zabumba em ritmo de baião, caixa, chimbal de ruído,
// baixo triangular, acordes e melodia. A intensidade (0-3) liga camadas
// conforme a horda cresce. Cada ETAPA da partida (quartos do relógio) tem seu
// tema, e cada BOSS tem trilha própria — do circo da CPI ao martelo do STF.
// Destaque eterno: o beep da urna eletrônica, patrimônio sonoro nacional.
// =============================================================================
import { VOICE_DUCK_LEVEL } from '../../shared/constants';

export interface Song {
  bpm: number;
  chords: string[][];
  bass: string[];
  bassSteps?: number[];
  kick?: number[];
  snare?: number[];
  hat?: number[];
  leadType?: OscillatorType;
  leadDur?: number;
  lead: (string | null)[];
}

export interface MoodOptions {
  level?: number;
  boss?: boolean;
  bossType?: string | null;
  stage?: number;
}

export interface SfxBank {
  urnaKey(): void;
  urnaConfirma(): void;
  urnaDourada(): void;   // Emenda de Relator (issue #93): fanfarra do card dourado
  levelUp(): void;
  kill(): void;
  gem(): void;
  hurt(): void;
  downed(): void;
  revive(): void;
  bossAlarm(): void;
  freeze(): void;
  nova(): void;
  pix(): void;
  defeat(): void;
  victory(): void;
  casinoOpen(): void;
  casinoTick(): void;
  casinoWin(): void;
  casinoLose(): void;
  casinoEvolve(): void;
}

// ---------------------------------------------------------------------------
// Parser de notas — n('C5') -> frequência. A4 = 440.
// ---------------------------------------------------------------------------
const NOTE = (() => {
  const idx: Record<string, number> = { C: -9, 'C#': -8, D: -7, 'D#': -6, E: -5, F: -4, 'F#': -3, G: -2, 'G#': -1, A: 0, 'A#': 1, B: 2 };
  return (name: string): number => {
    const m = name.match(/^([A-G]#?)(\d)$/);
    if (!m) return 0;
    return 440 * Math.pow(2, (idx[m[1]] + (Number(m[2]) - 4) * 12) / 12);
  };
})();
const n = NOTE;

// Groove de baião (zabumba): grave no 1, no "e-do-2" e resposta seca.
// Cada tema pode sobrescrever com groove próprio (kick/snare/hat/bassSteps).
const KICK_PAT = [0, 3, 8, 11];        // dentro de cada compasso de 16
const SNARE_PAT = [6, 14];
const HAT_PAT = [0, 2, 4, 6, 8, 10, 12, 14];
const BASS_BAIAO = [0, 3, 8, 11];
const BASS_8THS = [0, 2, 4, 6, 8, 10, 12, 14];

// ---------------------------------------------------------------------------
// TEMAS DE ETAPA — a partida (20min) dividida em quatro atos, do comício
// festivo à apuração em pânico. Sobe meio tom de tensão a cada quarto.
// ---------------------------------------------------------------------------

// Etapa 1 — "Campanha": carnaval em Dó maior — C / Am / F / G
const SONG_CAMPANHA: Song = {
  bpm: 148,
  chords: [['C3', 'E4', 'G4'], ['A3', 'E4', 'A4'], ['F3', 'F4', 'A4'], ['G3', 'F4', 'B4']],
  bass: ['C2', 'A1', 'F2', 'G2'],
  // 64 passos de melodia (null = pausa) — pergunta (2 compassos) e resposta
  lead: [
    'C5', null, 'E5', null, 'G5', null, 'C6', null, 'A5', null, 'G5', 'E5', 'G5', null, null, null,
    'A5', null, 'C6', null, 'A5', null, 'E5', null, 'G5', 'A5', 'G5', 'E5', 'D5', null, null, null,
    'F5', null, 'A5', null, 'C6', null, 'A5', null, 'G5', null, 'E5', 'C5', 'E5', null, null, null,
    'D5', 'E5', 'D5', null, 'B4', null, 'D5', null, 'C5', null, 'E5', 'G5', 'C6', null, 'G5', null,
  ],
};

// Etapa 2 — "Segundo Turno": baião em Ré menor, a festa azedou — Dm / Bb / Gm / A
const SONG_SEGUNDO_TURNO: Song = {
  bpm: 152,
  chords: [['D3', 'F4', 'A4'], ['A#2', 'F4', 'A#4'], ['G3', 'A#3', 'D4'], ['A3', 'C#4', 'E4']],
  bass: ['D2', 'A#1', 'G1', 'A1'],
  lead: [
    'D5', null, 'F5', null, 'A5', null, 'D6', null, 'C6', null, 'A5', 'F5', 'A5', null, null, null,
    'A#5', null, 'A5', null, 'G5', null, 'F5', null, 'E5', 'F5', 'E5', 'D5', 'C#5', null, null, null,
    'G5', null, 'A#5', null, 'D6', null, 'A#5', null, 'A5', null, 'F5', 'D5', 'F5', null, null, null,
    'E5', 'F5', 'E5', null, 'C#5', null, 'E5', null, 'D5', null, 'F5', 'A5', 'D6', null, 'A5', null,
  ],
};

// Etapa 3 — "Reta Final": Mi menor sincopado, todo mundo no limite — Em / C / Am / B
const SONG_RETA_FINAL: Song = {
  bpm: 158,
  chords: [['E3', 'G4', 'B4'], ['C3', 'E4', 'G4'], ['A3', 'C4', 'E4'], ['B2', 'D#4', 'F#4']],
  bass: ['E2', 'C2', 'A1', 'B1'],
  lead: [
    null, 'E5', null, 'G5', null, 'B5', null, 'E6', 'D6', null, 'B5', null, 'G5', null, 'B5', null,
    'C6', null, 'B5', 'A5', 'G5', null, 'F#5', null, 'G5', 'F#5', 'E5', null, 'D#5', null, 'E5', null,
    null, 'A5', null, 'C6', null, 'E6', null, 'C6', 'B5', null, 'G5', null, 'E5', null, 'G5', null,
    'F#5', 'G5', 'F#5', null, 'D#5', null, 'F#5', 'D#5', 'E5', null, 'B4', null, 'E5', null, null, null,
  ],
};

// Etapa 4 — "Apuração": Lá menor harmônico frenético, boca de urna do caos — Am / F / Dm / E
const SONG_APURACAO: Song = {
  bpm: 166,
  chords: [['A3', 'C4', 'E4'], ['F3', 'A3', 'C4'], ['D3', 'F4', 'A4'], ['E3', 'G#3', 'B3']],
  bass: ['A1', 'F1', 'D2', 'E2'],
  lead: [
    'A5', 'B5', 'C6', null, 'B5', 'C6', 'B5', 'A5', 'G#5', null, 'A5', null, 'E5', null, null, null,
    'F5', 'G5', 'A5', null, 'G5', 'A5', 'G5', 'F5', 'E5', null, 'F5', null, 'C5', null, null, null,
    'D6', null, 'C6', 'B5', 'A5', null, 'G5', 'F5', 'E5', null, 'F5', 'E5', 'D5', null, null, null,
    'E5', 'G#5', 'B5', 'E6', 'D6', 'B5', 'G#5', 'E5', 'A5', null, 'C6', null, 'A5', null, null, null,
  ],
};

const STAGE_SONGS: Song[] = [SONG_CAMPANHA, SONG_SEGUNDO_TURNO, SONG_RETA_FINAL, SONG_APURACAO];

// ---------------------------------------------------------------------------
// TEMAS DE BOSS — um por escândalo. Fallback genérico pra boss sem trilha.
// ---------------------------------------------------------------------------

// Fallback: tenso em Lá menor — Am / Am / F / E, baixo martelado
const SONG_BOSS: Song = {
  bpm: 156,
  chords: [['A3', 'C4', 'E4'], ['A3', 'C4', 'E4'], ['F3', 'A3', 'C4'], ['E3', 'G#3', 'B3']],
  bass: ['A1', 'A1', 'F1', 'E1'],
  bassSteps: BASS_8THS,
  lead: [
    'A4', null, 'A4', 'C5', 'B4', null, 'A4', null, 'E5', null, 'D5', 'C5', 'B4', null, null, null,
    'A4', null, 'A4', 'C5', 'B4', null, 'C5', null, 'E5', 'F5', 'E5', 'D5', 'C5', null, 'B4', null,
    'F5', null, 'E5', null, 'D5', null, 'C5', null, 'A4', null, 'C5', null, 'D5', 'C5', 'B4', null,
    'E5', null, 'B4', null, 'G#4', null, 'B4', null, 'A4', null, 'E4', null, 'A4', null, null, null,
  ],
};

const BOSS_SONGS: Record<string, Song> = {
  // Live das 19h — marchinha debochada em Sol mixolídio (fanfarra de bordão)
  live_19h: {
    bpm: 150,
    chords: [['G3', 'B3', 'D4'], ['F3', 'A3', 'C4'], ['C3', 'E4', 'G4'], ['G3', 'B3', 'D4']],
    bass: ['G1', 'F1', 'C2', 'G1'],
    bassSteps: BASS_8THS,
    lead: [
      'G5', null, 'G5', 'B5', 'D6', null, 'B5', null, 'F5', null, 'F5', 'A5', 'C6', null, 'A5', null,
      'G5', null, 'G5', 'B5', 'D6', null, 'E6', null, 'D6', 'C6', 'B5', 'A5', 'G5', null, null, null,
      'C6', null, 'B5', null, 'A5', null, 'G5', null, 'F5', null, 'E5', null, 'D5', null, 'F5', null,
      'G5', null, 'B5', null, 'D6', null, 'B5', null, 'G5', null, null, null, 'G5', null, null, null,
    ],
  },
  // Mensalão 2.0 — tema de espião: rastejante, cromático, mão no seu bolso
  mensalao2: {
    bpm: 122,
    chords: [['D3', 'F4', 'A4'], ['D3', 'F4', 'A4'], ['G3', 'A#3', 'D4'], ['A3', 'C#4', 'E4']],
    bass: ['D2', 'D2', 'G1', 'A1'],
    bassSteps: [0, 6, 8, 14],
    kick: [0, 8], snare: [12],
    leadType: 'triangle', leadDur: 0.22,
    lead: [
      'D5', null, null, null, 'D#5', null, null, null, 'E5', null, 'F5', null, 'E5', null, null, null,
      'D5', null, null, null, 'F5', null, 'E5', null, 'D5', null, 'C#5', null, 'D5', null, null, null,
      'G5', null, null, null, 'A#5', null, null, null, 'A5', null, 'G5', null, 'F5', null, null, null,
      'E5', null, 'F5', null, 'E5', null, 'C#5', null, 'A4', null, null, null, 'D5', null, null, null,
    ],
  },
  // Impeachment da Dilma — marcha de panelaço em Dó menor: caixa em TODO canto
  impeachment_dilma: {
    bpm: 144,
    chords: [['C3', 'D#4', 'G4'], ['G#2', 'C4', 'D#4'], ['F3', 'G#3', 'C4'], ['G3', 'B3', 'D4']],
    bass: ['C2', 'G#1', 'F1', 'G1'],
    kick: [0, 8], snare: [2, 4, 6, 10, 12, 14],
    lead: [
      'C5', 'C5', null, 'C5', 'D#5', null, 'C5', null, 'G5', null, 'F5', 'D#5', 'D5', null, null, null,
      'C5', 'C5', null, 'C5', 'D#5', null, 'G5', null, 'G#5', 'G5', 'F5', 'D#5', 'D5', null, null, null,
      'F5', null, 'F5', 'G5', 'G#5', null, 'G5', 'F5', 'D#5', null, 'C5', null, 'D#5', null, null, null,
      'D5', 'D5', null, 'D5', 'B4', null, 'D5', null, 'C5', null, 'G5', null, 'C6', null, null, null,
    ],
  },
  // CPI da Covid — a arena virou circo: galope cromático de picadeiro
  cpi_covid: {
    bpm: 172,
    chords: [['F3', 'A3', 'C4'], ['C3', 'E4', 'G4'], ['F3', 'A3', 'C4'], ['C3', 'E4', 'G4']],
    bass: ['F1', 'C2', 'F1', 'C2'],
    bassSteps: [0, 4, 8, 12],
    lead: [
      'C5', 'C#5', 'D5', 'D#5', 'E5', null, 'C5', null, 'F5', null, 'A5', null, 'C6', null, 'A5', null,
      'B4', 'C5', 'C#5', 'D5', 'D#5', null, 'E5', null, 'G5', null, 'E5', null, 'C5', null, null, null,
      'F5', 'F#5', 'G5', 'G#5', 'A5', null, 'F5', null, 'C6', null, 'A5', null, 'F5', null, null, null,
      'G5', 'A5', 'G5', 'F5', 'E5', null, 'C5', null, 'E5', null, 'G5', null, 'C6', null, null, null,
    ],
  },
  // STF vs Bolsonaro — solene e pesado em Lá frígio: cada nota é uma canetada
  stf_bolso: {
    bpm: 100,
    chords: [['A3', 'C4', 'E4'], ['A#2', 'D4', 'F4'], ['A3', 'C4', 'E4'], ['E3', 'G#3', 'B3']],
    bass: ['A1', 'A#1', 'A1', 'E1'],
    kick: [0, 4, 8, 12], snare: [8], hat: [0, 4, 8, 12],
    leadType: 'sawtooth', leadDur: 0.35,
    lead: [
      'A4', null, null, null, 'C5', null, null, null, 'A#4', null, null, null, 'A4', null, null, null,
      'D5', null, null, null, 'F5', null, null, null, 'E5', null, null, null, 'D5', null, null, null,
      'E5', null, null, null, 'C5', null, null, null, 'B4', null, null, null, 'G#4', null, null, null,
      'G#4', null, null, null, 'B4', null, null, null, 'A4', null, null, null, null, null, null, null,
    ],
  },
  // Orçamento Secreto — fantasmagórico: notas escassas, trítono, sem nota fiscal
  orcamento_secreto: {
    bpm: 126,
    chords: [['D3', 'F4', 'A4'], ['D3', 'F4', 'G#4'], ['G3', 'A#3', 'D4'], ['A3', 'C#4', 'E4']],
    bass: ['D2', 'D2', 'G#1', 'A1'],
    bassSteps: [0, 8],
    kick: [0], snare: [], hat: [4, 12],
    leadType: 'sine', leadDur: 0.3,
    lead: [
      'D5', null, null, null, null, null, 'F5', null, null, null, 'E5', null, null, null, null, null,
      'G#5', null, null, null, 'F5', null, null, null, 'D5', null, null, null, null, null, null, null,
      'D5', null, null, null, 'A#4', null, null, null, 'G5', null, null, null, null, null, null, null,
      'C#5', null, null, null, 'E5', null, null, null, 'D5', null, null, null, null, null, null, null,
    ],
  },
  // Lula no Planalto — samba de posse grandioso, maior com gosto de dívida — C / E / Am / F
  lula_planalto: {
    bpm: 140,
    chords: [['C3', 'E4', 'G4'], ['E3', 'G#3', 'B3'], ['A3', 'C4', 'E4'], ['F3', 'A3', 'C4']],
    bass: ['C2', 'E2', 'A1', 'F1'],
    lead: [
      'E5', null, 'G5', null, 'C6', null, 'B5', null, 'A5', null, 'G5', null, 'E5', null, 'G5', null,
      'G#5', null, 'B5', null, 'E6', null, 'D6', null, 'B5', null, 'G#5', null, 'E5', null, null, null,
      'A5', null, 'C6', null, 'E6', null, 'C6', null, 'B5', 'A5', 'G5', null, 'E5', null, null, null,
      'F5', 'G5', 'A5', null, 'C6', null, 'A5', null, 'G5', null, 'F5', null, 'D5', null, 'G5', null,
    ],
  },
  // 8 de Janeiro — boss final: Mi frígio implacável, bumbo dobrado, tudo quebrando
  oito_jan_invasao: {
    bpm: 178,
    chords: [['E3', 'G3', 'B3'], ['F3', 'A3', 'C4'], ['G3', 'B3', 'D4'], ['F3', 'A3', 'C4']],
    bass: ['E1', 'F1', 'G1', 'F1'],
    bassSteps: BASS_8THS,
    kick: BASS_8THS, snare: [4, 12],
    lead: [
      'E5', 'E5', null, 'E5', 'F5', null, 'E5', null, 'B5', null, 'A5', 'G5', 'F5', null, null, null,
      'F5', 'F5', null, 'F5', 'G5', null, 'F5', null, 'C6', null, 'A5', 'F5', 'E5', null, null, null,
      'G5', 'G5', null, 'G5', 'A5', null, 'G5', null, 'D6', null, 'B5', 'A5', 'G5', null, null, null,
      'F5', null, 'E5', 'F5', 'A5', null, 'G5', 'F5', 'E5', null, 'E5', 'E5', 'E5', null, null, null,
    ],
  },
};

// ---------------------------------------------------------------------------
// Motor de áudio — encapsula contexto, sequenciador e efeitos
// ---------------------------------------------------------------------------
// Personagens com voz de seleção gravada em /assets/audio/<charId>_selection.mp3
const SELECTION_VOICES = new Set<string>(['lula', 'bolsonaro', 'dilma', 'marinasilva', 'datena']);

// Volumes BASE das trilhas em HTMLAudio — o mix relativo entre elas é
// cláusula pétrea; o volume master do slider só multiplica por cima.
const MENU_MUSIC_BASE = 0.5;
const VOICE_BASE = 0.8;

// Volume master persistido entre sessões (padrão da casa: chaves bs_*)
const VOLUME_KEY = 'bs_volume';

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private muted = false;

  // Volume master (0..1) — manda no GainNode dos SFX sintetizados e
  // multiplica os volumes base das trilhas em HTMLAudio
  private volume = 1;
  private master: GainNode | null = null;

  constructor() {
    // Restaura o volume da sessão passada (se o eleitor já regulou o som)
    const raw = localStorage.getItem(VOLUME_KEY);
    const v = raw === null ? NaN : Number(raw);
    if (Number.isFinite(v)) this.volume = Math.min(1, Math.max(0, v));
  }

  // Buffer de ruído branco (bateria)
  private noiseBuf: AudioBuffer | null = null;

  // Rate-limit dos SFX de horda
  private killCd = 0;
  private gemCd = 0;

  // Estado do sequenciador
  private bgmTimer: ReturnType<typeof setInterval> | null = null;
  private stepIdx = 0;
  private nextStepTime = 0;
  private currentSong: Song = SONG_CAMPANHA;
  private pendingSong: Song | null = null;
  private intensity = 1; // 0 calmaria, 1 padrão, 2 horda grossa, 3 apocalipse

  // Música do menu — mp3 em loop; a vitrola procedural fica pra partida.
  // `scene` lembra qual trilha vale agora (pro unmute religar a certa).
  private menuMusic: HTMLAudioElement | null = null;
  private scene: 'menu' | 'game' = 'menu';

  private ac(): AudioContext {
    if (!this.ctx) {
      const Ctor = window.AudioContext
        ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext!;
      this.ctx = new Ctor();
      // Mesa de som central: TODO SFX/BGM sintetizado sai por este gain;
      // o slider de volume gira só este botão
      this.master = this.ctx.createGain();
      this.master.gain.value = this.volume;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
    return this.ctx;
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (m) {
      this.stopBgm();
      this.stopMenuMusic();
    } else if (this.scene === 'game') {
      this.startBgm();
    } else {
      this.startMenuMusic();
    }
  }

  // Primeiro gesto do jogador: o navegador libera o áudio, então liga a
  // trilha da cena atual (menu ou partida retomada por reconexão)
  unlock(): void {
    if (this.muted) return;
    if (this.scene === 'game') this.startBgm(); else this.startMenuMusic();
  }

  startMenuMusic(): void {
    this.scene = 'menu';
    this.stopBgm();
    if (this.muted) return;
    if (!this.menuMusic) {
      this.menuMusic = new Audio('/assets/audio/menu_theme.mp3');
      this.menuMusic.loop = true;
    }
    this.applyVolumes(); // respeita volume master E ducking de voz no ar
    void this.menuMusic.play().catch(() => { /* autoplay bloqueado antes do 1º gesto */ });
  }

  stopMenuMusic(): void {
    this.menuMusic?.pause();
  }

  isMuted(): boolean {
    return this.muted;
  }

  // Volume master (0..1): escala o gain dos sintetizados E as trilhas em
  // HTMLAudio (sempre multiplicando os volumes base). Zero = silêncio total.
  setVolume(v: number): void {
    this.volume = Math.min(1, Math.max(0, v));
    localStorage.setItem(VOLUME_KEY, String(this.volume));
    this.applyVolumes();
    for (const voice of this.voiceCache.values()) voice.volume = VOICE_BASE * this.volume;
  }

  // Ducking (issue #118): com voz gravada no ar, tudo que NÃO é voz abaixa
  // pra VOICE_DUCK_LEVEL e volta quando a última fala termina
  private duckFactor(): number {
    return this.playingVoices.size ? VOICE_DUCK_LEVEL : 1;
  }

  // Mesa de som + música do menu num só lugar: volume master x ducking.
  // A voz fica de fora de propósito — ela é a estrela do ducking.
  private applyVolumes(): void {
    const v = this.volume * this.duckFactor();
    // Rampa curta no gain evita clique audível na entrada/saída do ducking
    if (this.master && this.ctx) this.master.gain.setTargetAtTime(v, this.ctx.currentTime, 0.05);
    if (this.menuMusic) this.menuMusic.volume = MENU_MUSIC_BASE * v;
  }

  getVolume(): number {
    return this.volume;
  }

  // -------------------------------------------------------------------------
  // Blocos básicos de síntese
  // -------------------------------------------------------------------------
  private beep(freq: number, dur = 0.1, type: OscillatorType = 'square', gain = 0.08, when = 0): void {
    if (this.muted || !freq) return;
    const c = this.ac();
    const t = (when < 10 ? c.currentTime + when : when); // aceita offset ou tempo absoluto
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = type;
    osc.frequency.value = freq;
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g).connect(this.master!); // mesa de som: o slider manda
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private noise(t: number, dur: number, gain: number, filterType: BiquadFilterType, filterFreq: number): void {
    if (this.muted) return;
    const c = this.ac();
    if (!this.noiseBuf) {
      this.noiseBuf = c.createBuffer(1, c.sampleRate * 0.3, c.sampleRate);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    }
    const src = c.createBufferSource();
    src.buffer = this.noiseBuf;
    const f = c.createBiquadFilter();
    f.type = filterType; f.frequency.value = filterFreq;
    const g = c.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(this.master!);
    src.start(t); src.stop(t + dur + 0.02);
  }

  private kick(t: number): void {
    if (this.muted) return;
    const c = this.ac();
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, t);
    osc.frequency.exponentialRampToValueAtTime(42, t + 0.11);
    g.gain.setValueAtTime(0.32, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.16);
    osc.connect(g).connect(this.master!);
    osc.start(t); osc.stop(t + 0.2);
  }

  private snare(t: number): void {
    this.noise(t, 0.09, 0.14, 'bandpass', 1900);
    this.beep(196, 0.05, 'triangle', 0.06, t);
  }

  private hat(t: number, open = false): void {
    this.noise(t, open ? 0.09 : 0.035, 0.05, 'highpass', 8200);
  }

  // -------------------------------------------------------------------------
  // Vozes gravadas — mp3 em /assets/audio/. Toda voz passa por playVoice, que
  // cuida do ducking da trilha (issue #118): genérico de propósito, a seleção
  // de personagem usa hoje e os bordões de boss vão usar amanhã.
  // -------------------------------------------------------------------------
  private readonly voiceCache = new Map<string, HTMLAudioElement>();
  // Vozes tocando AGORA: enquanto houver uma, a trilha fica abaixada. Set (e
  // não boolean) porque seleção spammada sobrepõe falas — a música só volta
  // quando a última terminar.
  private readonly playingVoices = new Set<HTMLAudioElement>();

  selectVoice(charId: string): void {
    if (!SELECTION_VOICES.has(charId) || this.muted) return;
    this.playVoice(`/assets/audio/${charId}_selection.mp3`, charId);
  }

  private playVoice(url: string, cacheKey: string): void {
    let voice = this.voiceCache.get(cacheKey);
    if (!voice) {
      const el = new Audio(url);
      el.volume = VOICE_BASE * this.volume;
      // Fim, stop programático ou erro de carga: solta o ducking
      const release = (): void => {
        this.playingVoices.delete(el);
        this.applyVolumes();
      };
      el.addEventListener('ended', release);
      el.addEventListener('pause', release);
      el.addEventListener('error', release);
      this.voiceCache.set(cacheKey, el);
      voice = el;
    }
    voice.currentTime = 0;
    this.playingVoices.add(voice);
    this.applyVolumes();
    void voice.play().catch(() => {
      // Autoplay bloqueado antes do 1º gesto: sem fala, sem ducking
      this.playingVoices.delete(voice);
      this.applyVolumes();
    });
  }

  // -------------------------------------------------------------------------
  // SFX — arrow functions pra poder passar o método solto sem perder o this
  // -------------------------------------------------------------------------
  readonly sfx: SfxBank = {
    urnaKey: () => { this.beep(880, 0.06, 'square', 0.1); },
    // O icônico "tii-tiiii" do CONFIRMA
    urnaConfirma: () => { this.beep(1046, 0.12, 'square', 0.12); this.beep(1318, 0.35, 'square', 0.12, 0.14); },
    // Emenda de Relator dourada: arpejo cintilante + brilho agudo no topo
    urnaDourada: () => {
      [784, 1046, 1318, 1568, 2093].forEach((f, i) => this.beep(f, 0.1, 'triangle', 0.11, i * 0.06));
      this.beep(2637, 0.28, 'sine', 0.07, 0.32);
      this.beep(3136, 0.22, 'sine', 0.05, 0.4);
    },
    levelUp: () => { [523, 659, 784, 1046].forEach((f, i) => this.beep(f, 0.12, 'square', 0.09, i * 0.07)); },
    kill: () => {
      const now = performance.now();
      if (now - this.killCd < 70) return; this.killCd = now; // rate-limit: horda morre em atacado
      this.beep(300 + Math.random() * 200, 0.05, 'square', 0.045);
      this.noise(this.ac().currentTime, 0.04, 0.05, 'bandpass', 900);
    },
    gem: () => {
      const now = performance.now();
      if (now - this.gemCd < 60) return; this.gemCd = now;
      this.beep(900 + Math.random() * 500, 0.05, 'sine', 0.05);
    },
    hurt: () => { this.beep(110, 0.15, 'sawtooth', 0.12); this.beep(80, 0.2, 'sawtooth', 0.1, 0.05); },
    downed: () => { [300, 250, 200, 150].forEach((f, i) => this.beep(f, 0.2, 'sawtooth', 0.1, i * 0.15)); },
    revive: () => { [400, 600, 900].forEach((f, i) => this.beep(f, 0.15, 'triangle', 0.1, i * 0.1)); },
    bossAlarm: () => { for (let i = 0; i < 4; i++) this.beep(i % 2 ? 220 : 330, 0.25, 'sawtooth', 0.12, i * 0.28); },
    freeze: () => { this.beep(1200, 0.3, 'sine', 0.08); this.beep(600, 0.4, 'sine', 0.06, 0.1); },
    nova: () => { this.beep(70, 0.4, 'sawtooth', 0.16); this.beep(50, 0.5, 'square', 0.1, 0.05); },
    pix: () => { this.beep(987, 0.08, 'sine', 0.1); this.beep(1318, 0.12, 'sine', 0.1, 0.09); },
    defeat: () => { [392, 370, 349, 330].forEach((f, i) => this.beep(f, 0.4, 'sawtooth', 0.12, i * 0.35)); },
    victory: () => { [523, 659, 784, 1046, 784, 1046].forEach((f, i) => this.beep(f, 0.18, 'square', 0.1, i * 0.16)); },
    // Cassino da Maleta: fanfarra de abertura, tique de ficha e o veredito
    casinoOpen: () => { [523, 659, 784, 1046, 1318].forEach((f, i) => this.beep(f, 0.1, 'square', 0.09, i * 0.06)); },
    casinoTick: () => { this.beep(1400, 0.03, 'square', 0.05); },
    casinoWin: () => { [1046, 1318, 1568, 2093, 1568, 2093].forEach((f, i) => this.beep(f, 0.13, 'square', 0.1, i * 0.11)); },
    casinoLose: () => { [392, 370, 349, 262].forEach((f, i) => this.beep(f, 0.3, 'sawtooth', 0.09, i * 0.24)); },
    // GOLPE DE MESTRE (#98): fanfarra de evolução — arpejo maior subindo duas
    // oitavas e fechando num acorde sustentado (a posse da arma suprema)
    casinoEvolve: () => {
      [523, 659, 784, 1046, 1318, 1568, 2093].forEach((f, i) => this.beep(f, 0.12, 'square', 0.1, i * 0.09));
      [1046, 1318, 1568].forEach((f) => this.beep(f, 0.55, 'triangle', 0.08, 0.68));
      this.beep(2093, 0.55, 'square', 0.06, 0.68);
    },
  };

  // -------------------------------------------------------------------------
  // MÚSICA — sequenciador de 64 passos (4 compassos de 16ths)
  // -------------------------------------------------------------------------

  // Chamado pelo jogo a cada snapshot: adapta a música ao caos em tela.
  // stage (0-3) = quarto da partida; bossType troca pra trilha do escândalo da vez.
  setMood({ level = 1, boss = false, bossType = null, stage = 0 }: MoodOptions = {}): void {
    this.intensity = Math.max(0, Math.min(3, level));
    const want = boss
      ? ((bossType != null ? BOSS_SONGS[bossType] : undefined) ?? SONG_BOSS)
      : (STAGE_SONGS[Math.max(0, Math.min(3, stage))] ?? SONG_CAMPANHA);
    if (want !== this.currentSong) this.pendingSong = want;
  }

  private scheduleStep(i: number, t: number): void {
    const bar = Math.floor(i / 16) % 4;
    const s = i % 16;
    const song = this.currentSong;

    // Troca de tema só na virada de compasso (a banda não para no meio)
    if (s === 0 && this.pendingSong) { this.currentSong = this.pendingSong; this.pendingSong = null; }

    // BATERIA (intensidade >= 1) — groove padrão de baião, ou o do tema
    if (this.intensity >= 1) {
      if ((song.kick ?? KICK_PAT).includes(s)) this.kick(t);
      if ((song.snare ?? SNARE_PAT).includes(s)) this.snare(t);
      if ((song.hat ?? HAT_PAT).includes(s)) this.hat(t);
    }
    if (this.intensity >= 3 && s % 2 === 1) this.hat(t);            // chimbal dobrado no caos
    if (this.intensity >= 2 && s === 15) this.hat(t, true);         // abertura na virada

    // BAIXO: baião marca grave por padrão; temas podem martelar; caos dobra oitava
    const bassFreq = n(song.bass[bar]);
    if ((song.bassSteps ?? BASS_BAIAO).includes(s)) {
      this.beep(bassFreq, 0.16, 'triangle', 0.16, t);
      if (this.intensity >= 3) this.beep(bassFreq * 2, 0.1, 'triangle', 0.07, t);
    }

    // ACORDES: stabs no contratempo (intensidade >= 2)
    if (this.intensity >= 2 && (s === 4 || s === 12)) {
      for (const note of song.chords[bar]) this.beep(n(note), 0.1, 'square', 0.028, t);
    }

    // MELODIA: sempre presente; mais alta com a intensidade; timbre por tema
    const leadNote = song.lead[i % 64];
    if (leadNote) {
      const vol = this.intensity === 0 ? 0.035 : this.intensity >= 2 ? 0.06 : 0.05;
      const dur = song.leadDur ?? 0.14;
      const type = song.leadType ?? 'square';
      this.beep(n(leadNote), dur, type, vol, t);
      if (this.intensity >= 2) this.beep(n(leadNote) / 2, dur, type, vol * 0.4, t); // dobra em oitava
    }
  }

  startBgm(): void {
    this.scene = 'game';
    this.stopMenuMusic();
    if (this.muted || this.bgmTimer) return;
    const c = this.ac();
    this.stepIdx = 0;
    this.nextStepTime = c.currentTime + 0.06;
    // Lookahead: agenda ~180ms à frente com precisão de sample
    this.bgmTimer = setInterval(() => {
      const stepDur = 60 / this.currentSong.bpm / 4;
      while (this.nextStepTime < this.ac().currentTime + 0.18) {
        this.scheduleStep(this.stepIdx, this.nextStepTime);
        this.stepIdx = (this.stepIdx + 1) % 64;
        this.nextStepTime += stepDur;
      }
    }, 60);
  }

  stopBgm(): void {
    if (this.bgmTimer !== null) clearInterval(this.bgmTimer);
    this.bgmTimer = null;
  }
}

// Singleton — o jogo inteiro toca na mesma vitrola
export const audio = new AudioEngine();
